import React, { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import {
    PAGE_STATE_DURING_EVENT,
    PAGE_STATE_DURING_EVENT_MULTI_SESSION,
    PAGE_STATE_PRE_EVENT,
    PAGE_STATE_CHECK_IN,
    EMBED_TYPE_INTROVOKE,
    EMBED_TYPE_ZOOM,
    EMBED_TYPE_GOLDCAST,
} from 'common/constants/virtualEvent';
import { TouchpointDataProvider } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { VIRTUAL } from 'common/components/TouchpointEditor/constants/touchpoints';
import './index.styl';

import Livestream from '../states/Livestream/components/Livestream';
import PreCheckIn from '../states/PreCheckIn/components/PreCheckIn';
import CheckIn from '../states/CheckIn/components/CheckIn';
import DuringEvent from '../states/DuringEvent/components/DuringEvent';

export default function VirtualEventRenderer({
    isBuilderSaving,
    guest,
    event,
    colors,
    fonts,
    logos,
    currentPageState,
    embedConfig,
    hasAltEmbed,
    altEmbedConfig,
    engine,
    updateHasAltEmbed,
    openEmbedModal,
    openSplashStudioSettingsModal,
    onCheckInClick,
    shouldPreview,
}) {
    const history = useHistory();
    const { search } = useLocation();
    const isGalleryViewEnabled =
        embedConfig.type === EMBED_TYPE_ZOOM && embedConfig?.options?.[EMBED_TYPE_ZOOM]?.galleryViewEnabled;
    const isSplashStudio = embedConfig.type === EMBED_TYPE_INTROVOKE;
    const isGoldcast = embedConfig.type === EMBED_TYPE_GOLDCAST;
    const onCheckIn = () =>
        !shouldPreview ? onCheckInClick() : history.replace(`../${PAGE_STATE_DURING_EVENT}/preview${search}`);

    const embeds = {
        data: {
            embedConfig,
            altEmbedConfig,
            hasAltEmbed,
            shouldRefreshEmbeds: !isBuilderSaving,
            shouldRenderAltEmbed: !isGalleryViewEnabled && !isSplashStudio && !isGoldcast,
        },
        actions: {
            updateHasAltEmbed,
            openEmbedModal,
            openSplashStudioSettingsModal,
        },
    };

    const dataAdaptedForDataProvider = useMemo(
        () => ({
            event: {
                id: event.id,
                title: event.title,
                startTime: event.startTime,
                multisession: event.multisession,
                touchpoint: VIRTUAL,
            },
            theme: {
                colors: colors.sort((a, b) => a.id - b.id).map(({ value }) => value),
                availableFonts: fonts.sort((a, b) => a.id - b.id).map(({ value }) => value),
                logos: logos.reduce((acc, { id, ...logo }) => ({ ...acc, [id]: logo }), {}),
            },
        }),
        [event, colors, fonts, logos],
    );

    const getActivePageStateComponent = () => {
        switch (currentPageState) {
            case PAGE_STATE_DURING_EVENT_MULTI_SESSION:
                return (
                    <Livestream
                        guest={guest}
                        structure={get(engine, ['states', currentPageState, 'structure'])}
                        currentPageState={currentPageState}
                    />
                );
            case PAGE_STATE_PRE_EVENT:
                return (
                    <PreCheckIn
                        structure={get(engine, ['states', currentPageState, 'structure'])}
                        currentPageState={currentPageState}
                    />
                );
            case PAGE_STATE_CHECK_IN:
                return (
                    <CheckIn
                        structure={get(engine, ['states', currentPageState, 'structure'])}
                        currentPageState={currentPageState}
                        onCheckInClick={onCheckIn}
                        isTermsAndConditionsNotificationEnabled={event.isTermsAndConditionsNotificationEnabled}
                    />
                );
            case PAGE_STATE_DURING_EVENT:
                return (
                    <DuringEvent
                        structure={get(engine, ['states', currentPageState, 'structure'])}
                        currentPageState={currentPageState}
                        guest={guest}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <TouchpointDataProvider
            event={dataAdaptedForDataProvider.event}
            theme={dataAdaptedForDataProvider.theme}
            embeds={embeds}
        >
            {getActivePageStateComponent()}
        </TouchpointDataProvider>
    );
}

VirtualEventRenderer.propTypes = {
    isBuilderSaving: PropTypes.bool,
    guest: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    colors: PropTypes.array.isRequired,
    fonts: PropTypes.array.isRequired,
    logos: PropTypes.array.isRequired,
    currentPageState: PropTypes.string,
    embedConfig: PropTypes.object.isRequired,
    hasAltEmbed: PropTypes.bool.isRequired,
    altEmbedConfig: PropTypes.object,
    engine: PropTypes.object,
    onCheckInClick: PropTypes.func,
    updateHasAltEmbed: PropTypes.func,
    openEmbedModal: PropTypes.func,
    shouldPreview: PropTypes.bool,
    openSplashStudioSettingsModal: PropTypes.func,
};

VirtualEventRenderer.defaultProps = {
    isBuilderSaving: undefined,
    altEmbedConfig: {},
    engine: {},
    onCheckInClick: noop,
    updateHasAltEmbed: noop,
    openEmbedModal: noop,
    shouldPreview: false,
    currentPageState: null,
    openSplashStudioSettingsModal: noop,
};
